import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useLayouts, useUi, useBlocks } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import hearCandy from "@src/assets/images/hear-candy-logo.png";
import yamaha from "@src/assets/images/yamaha-logo.svg?url";
import { messages } from "./Case1TechnicalPov.messages";
import styles from "./Case1TechnicalPov.module.scss";

import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import { AudioPlayerBlock } from "@src/lms/jelly/extensions/blocks/audioPlayer/AudioPlayerBlock";
import * as appConfig from "appConfig";

import { RecordingsInfoModal } from "./components/RecordingsInfoModal";
import { PhotoView } from "@src/lms/jelly/extensions/layouts/photo-view/PhotoView";
import { SignalFlowModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/SignalFlowModal";
import { SpeakerDistributionModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/SpeakerDistributionModal";
import { SystemTuningModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/SystemTuningModal";
import { SpeakerDistributionSlide } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/SpeakerDistributionSlide";
import { SimulationsModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/SimulationsModal";
import { VenueModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/VenueModal";
import { BlachnioModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/BlachnioModal";
import { BakerModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/BakerModal";
import { GirresModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/technical-pov/components/GirresModal";
import { BuffModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/artistic-pov/components/BuffModal";

interface Case1TechnicalPovProps {
  title: string;
}

const ACCORDION_IDS = {
  productionProcess: "production-process",
  interviews: "interviews",
  recordings: "recordings",
} as const;

const PRODUCTION_ACCORDION_IDS = {
  venue: "venue",
  speakerDistribution: "speaker-distribution",
  signalFlow: "signal-flow",
  systemTuning: "system-tuning",
} as const;

type CaseSlide = "default" | "venue" | "speaker-distribution" | "signal-flow" | "tuning" | "recordings" | "interviews";

interface Case1ContextData {
  setActiveSlide: (slide: CaseSlide) => void;
  activeSlide: CaseSlide;
}

const Case1Context = React.createContext<Case1ContextData>({
  activeSlide: "default",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

export const Case1TechnicalPov: React.FC<Case1TechnicalPovProps> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<CaseSlide>("default");

  return (
    <Case1Context.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <Case1TechnicalPovContent title={title} />
    </Case1Context.Provider>
  );
};

const Case1TechnicalPovContent: React.FC<Case1TechnicalPovProps> = ({ title }) => {
  const accordion = <CaseAccordion title={title} />;
  const { activeSlide } = useContext(Case1Context);
  const layout = useLayouts();
  const { formatMessage } = useIntl();

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "default":
      content = (
        <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/room-default.svg`} alt={"room"} />
      );
      break;
    case "recordings":
      content = (
        <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/room-recordings.svg`} alt={"room"} />
      );
      break;
    case "venue":
      content = (
        <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/room-venue.svg`} alt={"room"} />
      );
      break;
    case "tuning":
      content = (
        <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/room-tuning.svg`} alt={"room"} />
      );
      break;
    case "interviews":
      content = (
        <img src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/room-interviews.svg`} alt={"room"} />
      );
      break;
    case "speaker-distribution":
      content = <SpeakerDistributionSlide />;
      break;
    case "signal-flow":
      content = (
        <layout.controlsToggleExtraContent
          controlsPosition={"bottom"}
          controls={[
            {
              type: "button",
              options: {},
              items: [
                { name: formatMessage(messages.signalFlowMenuLogical) },
                { name: formatMessage(messages.signalFlowMenuPhysical) },
              ],
            },
          ]}
          extraContent={[]}
        >
          {[
            <PhotoView
              key={"logical"}
              imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/signal-flow-logical.svg`}
              imgMode={"contain"}
            />,
            <PhotoView
              key={"physical"}
              imgSrc={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/signal-flow-physical.svg`}
              imgMode={"contain"}
            />,
          ]}
        </layout.controlsToggleExtraContent>
      );
      break;
  }

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.content}>{content}</div>
    </StandardCase>
  );
};

const CaseAccordion: React.FC<Case1TechnicalPovProps> = () => {
  const ui = useUi();
  const block = useBlocks();
  const { formatMessage } = useIntl();

  //todo: refactor modal state management
  const [recordingInfoModalOpen, setRecordingInfoModalOpen] = useState(false);
  const handleRecordingsInfoModalToggle = useCallback(() => setRecordingInfoModalOpen((open) => !open), []);
  const [systemTuningModalOpen, setSystemTuningModalOpen] = useState(false);
  const handleSystemTuningModalToggle = useCallback(() => setSystemTuningModalOpen((open) => !open), []);
  const [speakerDistributionModalOpen, setSpeakerDistributionModalOpen] = useState(false);
  const handleSpeakerDistributionModalToggle = useCallback(() => setSpeakerDistributionModalOpen((open) => !open), []);
  const [signalFlowModalOpen, setSignalFlowModalOpen] = useState(false);
  const handleSignalFlowModalToggle = useCallback(() => setSignalFlowModalOpen((open) => !open), []);
  const [simulationModalOpen, setSimulationModalOpen] = useState(false);
  const handleSimulationModalToggle = useCallback(() => setSimulationModalOpen((open) => !open), []);
  const [venueModalOpen, setVenueModalOpen] = useState(false);
  const handleVenueModalToggle = useCallback(() => setVenueModalOpen((open) => !open), []);
  const [bakerModalOpen, setBakerModalOpen] = useState(false);
  const handleBakerModalToggle = useCallback(() => setBakerModalOpen((open) => !open), []);
  const [blachnioModalOpen, setBlachnioModalOpen] = useState(false);
  const handleBlachnioModalToggle = useCallback(() => setBlachnioModalOpen((open) => !open), []);
  const [buffModalOpen, setBuffModalOpen] = useState(false);
  const handleBuffModalToggle = useCallback(() => setBuffModalOpen((open) => !open), []);
  const [girresModalOpen, setGirresModalOpen] = useState(false);
  const handleGirresModalToggle = useCallback(() => setGirresModalOpen((open) => !open), []);

  const INTERVIEWERS = [
    {
      title: messages.interviews1Title,
      description: messages.interviews1Description,
      bioCallback: handleBakerModalToggle,
      thumbnail: "baker.png",
      videoSrc: "https://youtu.be/v56zXnXzr5w",
    },
    {
      title: messages.interviews2Title,
      description: messages.interviews2Description,
      bioCallback: handleBlachnioModalToggle,
      thumbnail: "blachnio.png",
      videoSrc: "https://youtu.be/mHahq8yeEyQ",
    },
    {
      title: messages.interviews4Title,
      description: messages.interviews4Description,
      bioCallback: handleGirresModalToggle,
      thumbnail: "girres.png",
      videoSrc: "https://youtu.be/gYmVQpZ1QR0",
    },
    {
      title: messages.interviews3Title,
      description: messages.interviews3Description,
      bioCallback: handleBuffModalToggle,
      thumbnail: "buff.png",
      videoSrc: "https://www.youtube.com/watch?v=_U0dEK1R2u0",
    },
  ];

  return (
    <div className={styles.leftPanel}>
      <ui.Scrollbars>
        <div className={styles.leftPanelWrapper}>
          <div className={styles.accordion}>
            <Accordion allowMultiOpen={false} defaultOpen={[]}>
              <MainAccordionObserver />
              <AccordionItem
                id={ACCORDION_IDS.productionProcess}
                title={formatMessage(messages.accordionItem1Title)}
                unmountChildrenWhenHidden
              >
                <Accordion allowMultiOpen={false} defaultOpen={[]}>
                  <ProductionAccordionObserver />
                  <AccordionItem
                    id={PRODUCTION_ACCORDION_IDS.venue}
                    title={formatMessage(messages.accordionItem1aTitle)}
                    theme={"ghost"}
                  >
                    <p>{formatMessage(messages.accordionItem1aDescription)}</p>
                    <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleVenueModalToggle}>
                      {formatMessage(messages.view360Image)}
                    </ui.Button>
                  </AccordionItem>
                  <AccordionItem
                    id={PRODUCTION_ACCORDION_IDS.speakerDistribution}
                    title={formatMessage(messages.accordionItem1bTitle)}
                    theme={"ghost"}
                  >
                    <p>{formatMessage(messages.accordionItem1bDescription)}</p>
                    <ui.Button
                      shape={"normal"}
                      theme={"primary"}
                      size={"small"}
                      onClick={handleSpeakerDistributionModalToggle}
                    >
                      {formatMessage(messages.readMore)}
                    </ui.Button>
                    <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleSimulationModalToggle}>
                      {formatMessage(messages.simulations)}
                    </ui.Button>
                  </AccordionItem>
                  <AccordionItem
                    id={PRODUCTION_ACCORDION_IDS.signalFlow}
                    title={formatMessage(messages.accordionItem1cTitle)}
                    theme={"ghost"}
                  >
                    <p>{formatMessage(messages.accordionItem1cDescription)}</p>
                    <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleSignalFlowModalToggle}>
                      {formatMessage(messages.readMore)}
                    </ui.Button>
                  </AccordionItem>
                  <AccordionItem
                    id={PRODUCTION_ACCORDION_IDS.systemTuning}
                    title={formatMessage(messages.accordionItem1dTitle)}
                    theme={"ghost"}
                  >
                    <p>{formatMessage(messages.accordionItem1dDescription)}</p>
                    <ui.Button
                      shape={"normal"}
                      theme={"primary"}
                      size={"small"}
                      onClick={handleSystemTuningModalToggle}
                    >
                      {formatMessage(messages.readMore)}
                    </ui.Button>
                  </AccordionItem>
                </Accordion>
              </AccordionItem>
              <AccordionItem id={ACCORDION_IDS.interviews} title={formatMessage(messages.accordionItem2Title)}>
                <div className={styles.interviews}>
                  {INTERVIEWERS.map((data) => (
                    <>
                      <div className={styles.entry}>
                        <div className={styles.videoThumb}>
                          <block.videoButton
                            theme={"primary"}
                            shape={"invisible"}
                            videoBlock={{
                              src: data.videoSrc,
                              provider: "youtube",
                            }}
                            text={renderToString(
                              <img
                                src={`${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/interviews/${data.thumbnail}`}
                                alt={"thumbnail"}
                              />
                            )}
                          />
                        </div>
                        <div className={styles.text}>
                          <b>{formatMessage(data.title)}</b>
                          <br />
                          {formatMessage(data.description)}
                        </div>
                      </div>
                      <div className={styles.bioButtonWrapper}>
                        <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={data.bioCallback}>
                          {formatMessage(messages.bioNote)}
                        </ui.Button>
                      </div>
                    </>
                  ))}
                </div>
              </AccordionItem>
              <AccordionItem
                id={ACCORDION_IDS.recordings}
                title={formatMessage(messages.accordionItem3Title)}
                preventPrerender
                unmountChildrenWhenHidden
              >
                {/*//TODO: Replace src to minified version once received*/}
                <AudioPlayerBlock
                  autoplayOnTrackSwitch
                  samples={[
                    {
                      src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-ambeo.wav`,
                      title: `0:20 - "Echoes of Space" (Ambeo)`,
                    },
                    {
                      src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-zoom-vr.wav`,
                      title: `0:35 - "Echoes of Space" (Zoom)`,
                    },
                    {
                      src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-zylia.wav`,
                      title: `0:19 - "Echoes of Space" (Zylia)`,
                    },
                  ]}
                />
                <div className={styles.collaboration}>
                  {formatMessage(messages.collaboration)}
                  <img src={hearCandy} alt="hear candy" />
                </div>
                {formatMessage(messages.accordionItem3Description)}
                <p>
                  <ui.Button
                    shape={"normal"}
                    theme={"primary"}
                    size={"small"}
                    onClick={handleRecordingsInfoModalToggle}
                  >
                    {formatMessage(messages.readMore)}
                  </ui.Button>
                </p>
              </AccordionItem>
            </Accordion>
          </div>

          <div className={styles.collaboration}>
            {formatMessage(messages.collaboration)}
            <img src={yamaha} alt="yamaha" />
          </div>
        </div>
      </ui.Scrollbars>
      <RecordingsInfoModal visible={recordingInfoModalOpen} onClose={handleRecordingsInfoModalToggle} />
      <SignalFlowModal visible={signalFlowModalOpen} onClose={handleSignalFlowModalToggle} />
      <SpeakerDistributionModal visible={speakerDistributionModalOpen} onClose={handleSpeakerDistributionModalToggle} />
      <SystemTuningModal visible={systemTuningModalOpen} onClose={handleSystemTuningModalToggle} />
      <SimulationsModal visible={simulationModalOpen} onClose={handleSimulationModalToggle} />
      <VenueModal visible={venueModalOpen} onClose={handleVenueModalToggle} />
      <BlachnioModal visible={blachnioModalOpen} onClose={handleBlachnioModalToggle} />
      <GirresModal visible={girresModalOpen} onClose={handleGirresModalToggle} />
      <BakerModal visible={bakerModalOpen} onClose={handleBakerModalToggle} />
      <BuffModal visible={buffModalOpen} onClose={handleBuffModalToggle} />
    </div>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case ACCORDION_IDS.recordings:
          slide = "recordings";
          break;
        case ACCORDION_IDS.interviews:
          slide = "interviews";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};

const ProductionAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case PRODUCTION_ACCORDION_IDS.venue:
          slide = "venue";
          break;
        case PRODUCTION_ACCORDION_IDS.speakerDistribution:
          slide = "speaker-distribution";
          break;
        case PRODUCTION_ACCORDION_IDS.signalFlow:
          slide = "signal-flow";
          break;
        case PRODUCTION_ACCORDION_IDS.systemTuning:
          slide = "tuning";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
